/**
 * Hook: GET /api/t/{slug}/jds/{jdId}/stage-counts — per-stage candidate counts.
 *
 * Phase 2.2 — feeds the per-stage count line beneath each stage label in
 * `<StageProgressionBar>` (spec §3.5). The toggle (`<HideCompletedToggle>`)
 * does NOT filter these counts — the bar always shows totals (spec §3.6).
 *
 * Cache key: `["stage-counts", tenantSlug, jdId]` (Architect §3).
 *
 * Response shape mirrors `app/schemas/pipeline_stages.py::StageCountsResponse`:
 *   `{ counts: [{stage_id, slug, name, count}] }`.
 *
 * Includes 0-count rows for stages with no candidates (I-20). Cross-tenant
 * `jd_id` returns 404; the hook surfaces it as null so the caller can
 * render the "no JD" empty state instead of an error toast.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";

interface StageCount {
  stage_id: string;
  slug: string;
  name: string;
  count: number;
}

interface StageCountsResponse {
  counts: StageCount[];
}

interface UseJdStageCountsOpts {
  jdId: string | undefined;
}

export function useJdStageCounts(
  opts: UseJdStageCountsOpts,
): UseQueryResult<StageCount[] | null, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { jdId } = opts;

  return useQuery<StageCount[] | null, ApiError>({
    queryKey: ["stage-counts", tenantSlug, jdId],
    queryFn: async () => {
      try {
        const r = await apiClient.get<StageCountsResponse>(
          `/api/t/${tenantSlug}/jds/${jdId}/stage-counts`,
        );
        return r.data.counts;
      } catch (err) {
        // 404: cross-tenant or never-existed JD. Treat as null so the
        // surface renders empty state instead of an error toast (mirrors
        // useRanking pattern).
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: !!tenantSlug && !!jdId,
    staleTime: 30_000,
  });
}
